import React, { useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  type MenuProps,
} from '@mui/material';
import {
  CloudUploadOutlined as CloudUploadOutlinedIcon,
  Close as CloseIcon,
  DeleteOutlined as DeleteOutlineIcon,
  DownloadOutlined as DownloadOutlinedIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyOffOutlined as KeyOffOutlinedIcon,
  KeyOutlined as KeyOutlinedIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
} from '@mui/icons-material';
import type { TApiApplicationDocument } from '~/modules/covenants/types.ts';
import { useDocListContext } from '~/modules/covenants/CovenantsDataApplicationForm/components/DocListContext.tsx';
import { appFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/styles.ts';
import {
  DELETE_SIGNATURE_LABEL,
  DOC_ACTION_LABELS,
  DOC_ACTION_MENU_ID_PREFIXES,
  DOC_ACTION_MENU_ORIGIN,
  TRANSPARENT_MENU_BACKDROP_STYLES,
} from '~/modules/covenants/CovenantsDataApplicationForm/constants.ts';
import { useRequiredDocumentUploadAction } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/useRequiredDocumentUploadAction.ts';
import { RequiredDocumentUploadConfirmModal } from '~/modules/covenants/CovenantsDataApplicationForm/components/RequiredDocumentUploadConfirmModal.tsx';
import {
  buildApplicationDocumentRowKey,
  canAttachSignature,
  canDeleteFile,
  canDeleteSignature,
} from '~/modules/covenants/utils.ts';
import { EMPTY_SIGNATURE_VALIDATION_DOCUMENT_STATE } from '~/modules/covenants/constants.tsx';
import { isFeatureOn } from '~/helpers/utils.ts';
import { isDocumentGenerationAvailable } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration.ts';
import { DocumentGenerationDialogs } from '~/modules/covenants/CovenantsDataApplicationForm/components/DocumentGenerationDialogs.tsx';
import type { TFinancialConditionFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import type { TIncomeStructureByKvedFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/types.ts';
import type {
  TCertificateSaveResult,
  TDocumentGenerationOptions,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/types.ts';
import type {
  StateAidCertificateType,
  TStateAidFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/types.ts';
import {
  DOCUMENT_GENERATION_LABELS,
  isDocumentRegenerationAvailable,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/constants.ts';
import { getCertificateDocumentFlags } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/utils.ts';
import { useDocumentGenerationFlow } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/useDocumentGenerationFlow.ts';

interface IDocActionsProps {
  mode: 'document' | 'file';
  doc: TApiApplicationDocument;
  groupHasFiles?: boolean;
  onOpenCovenants: () => void;
}

const actionMenuSlotProps: MenuProps['slotProps'] = {
  paper: { sx: appFormStyles.actionMenuPaper },
  backdrop: {
    invisible: true,
    sx: TRANSPARENT_MENU_BACKDROP_STYLES,
  },
};

export const DocActions: React.FC<IDocActionsProps> = ({
  mode,
  doc,
  groupHasFiles = false,
  onOpenCovenants,
}) => {
  const {
    isNewApplication,
    isReadOnly,
    localFiles,
    signatureValidationState: signatureValidationStateMap,
    onUploadForDoc,
    onUploadSignatureClick,
    onDeleteSignature,
    onPreviewDocument,
    onDownloadDocument,
    onDeleteDocument,
    onAddDocumentRow,
    documentGenerationCallbacks,
  } = useDocListContext();
  const {
    saveFinancialCondition,
    loadFinancialCondition,
    saveIncomeStructureByKved,
    loadIncomeStructureByKved,
    saveStateAidReceived,
    loadStateAidReceived,
    loadHistory,
    loadDocumentBody,
    generate,
  } = documentGenerationCallbacks;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);
  const {
    isGenerationModalOpen,
    isHistoryModalOpen,
    historicalBody,
    regenerationSource,
    stateAidCertificateType,
    setStateAidCertificateType,
    isRegenerationTypeSelection,
    openHistory,
    openNewDocument,
    openHistoricalDocument,
    openRegeneration,
    openRegenerationWithType,
    closeGeneration,
    closeHistory,
    closeGenerationAfterSuccess,
  } = useDocumentGenerationFlow({
    document: doc,
    isGenerating: isGeneratingDocument,
    onLoadDocumentBody: loadDocumentBody,
  });
  const isMenuOpen = Boolean(anchorEl);
  const { isStateAidReceived: isStateAidDocument } = getCertificateDocumentFlags(doc.documentId);
  const stableKey = buildApplicationDocumentRowKey(doc);
  const localFile = localFiles[stableKey];
  const signatureValidationState =
    signatureValidationStateMap[String(doc.documentId)] ??
    EMPTY_SIGNATURE_VALIDATION_DOCUMENT_STATE;
  const hasFile = !!doc.fileName || !!localFile?.file;
  const canShowAttachSignature = canAttachSignature(doc, localFile, signatureValidationState);
  const hasAttachedSignature =
    !!localFile?.signature ||
    !!localFile?.isExternal ||
    doc.isCustomSignFile ||
    !!doc.hasSignedFile ||
    signatureValidationState.hasGeneratedProtocol;
  const canRemovePendingRow = !!doc.isVirtualRow && !hasFile;
  const shouldShowGenerateDocumentAction = isDocumentGenerationAvailable(
    doc.documentId,
    isNewApplication,
    isFeatureOn('VITE_COVENANTS_DOCUMENT_GENERATION_ENABLED')
  );
  const canRegenerateDocument = isDocumentRegenerationAvailable(
    doc.documentId,
    doc.availableToRegenerate,
    isReadOnly
  );
  const documentMenuId = `${DOC_ACTION_MENU_ID_PREFIXES.document}-${stableKey}`;
  const fileMenuId = `${DOC_ACTION_MENU_ID_PREFIXES.file}-${stableKey}`;

  const handlePreviewClick = () => {
    onPreviewDocument(doc, localFile);
  };

  const handleDownloadClick = () => {
    onDownloadDocument(doc, localFile);
  };

  const handleUploadClick = () => {
    if (mode === 'document' && groupHasFiles) {
      onAddDocumentRow(doc);
      return;
    }

    onUploadForDoc(stableKey);
  };

  const handleUploadSignatureClick = () => {
    onUploadSignatureClick(stableKey, doc);
  };

  const handleDeleteSignatureClick = () => {
    onDeleteSignature(stableKey, doc);
  };

  const handleDeleteDocumentClick = () => {
    onDeleteDocument(stableKey);
  };

  const {
    isConfirmModalOpen,
    requiredUploadConfig,
    confirmableStatus,
    onUploadClick,
    onCloseConfirmModal,
    onConfirmUpload,
  } = useRequiredDocumentUploadAction({
    doc,
    isNewApplication,
    onUpload: handleUploadClick,
  });

  const shouldShowRequiredUploadAction =
    !hasFile && !!requiredUploadConfig && !isReadOnly && !canRemovePendingRow;
  const shouldShowReplaceUploadAction =
    !!requiredUploadConfig &&
    !isReadOnly &&
    !canRemovePendingRow &&
    !canShowAttachSignature &&
    !hasAttachedSignature;

  const handleMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl((currentAnchor) => (currentAnchor ? null : event.currentTarget));
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleGenerateDocument = async (options: TDocumentGenerationOptions): Promise<boolean> => {
    setIsGeneratingDocument(true);

    try {
      const isGenerated = await generate(doc, options);

      if (isGenerated) {
        closeGenerationAfterSuccess();
      }

      return isGenerated;
    } finally {
      setIsGeneratingDocument(false);
    }
  };

  const handleSaveFinancialCondition = (
    values: TFinancialConditionFormValues,
    version: number
  ): Promise<TCertificateSaveResult> => saveFinancialCondition(values, version);

  const handleLoadFinancialCondition = () => loadFinancialCondition(doc);

  const handleSaveIncomeStructureByKved = (
    values: TIncomeStructureByKvedFormValues,
    version: number
  ): Promise<TCertificateSaveResult> => saveIncomeStructureByKved(doc, values, version);

  const handleLoadIncomeStructureByKved = () => loadIncomeStructureByKved(doc);

  const handleSaveStateAidReceived = (
    type: StateAidCertificateType,
    values: TStateAidFormValues,
    version: number
  ): Promise<TCertificateSaveResult> => saveStateAidReceived(doc, type, values, version);

  const handleLoadStateAidReceived = (type: StateAidCertificateType) =>
    loadStateAidReceived(doc, type);

  const documentGenerationDialogs = (
    <DocumentGenerationDialogs
      document={doc}
      flow={{
        isGenerationModalOpen,
        isHistoryModalOpen,
        historicalBody,
        regenerationSource,
        stateAidCertificateType,
        setStateAidCertificateType,
        isRegenerationTypeSelection,
        openHistoricalDocument,
        openNewDocument,
        openRegenerationWithType,
        closeGeneration,
        closeHistory,
      }}
      modalProps={{
        isGenerating: isGeneratingDocument,
        onSaveFinancialCondition: handleSaveFinancialCondition,
        onLoadFinancialCondition: handleLoadFinancialCondition,
        onSaveIncomeStructureByKved: handleSaveIncomeStructureByKved,
        onLoadIncomeStructureByKved: handleLoadIncomeStructureByKved,
        onSaveStateAidReceived: handleSaveStateAidReceived,
        onLoadStateAidReceived: handleLoadStateAidReceived,
        stateAidCertificateType,
        stateAidYearPeriod: doc.yearPeriod,
        stateAidChildPeriod: doc.childPeriod,
        onGenerate: handleGenerateDocument,
      }}
      onLoadHistory={(page, size) => loadHistory(doc, page, size)}
      requiresStateAidType={isStateAidDocument}
    />
  );

  const runMenuAction = (action: () => void) => {
    handleMenuClose();
    action();
  };

  const fileMenuActions = useMemo(
    () => ({
      showUpload: shouldShowRequiredUploadAction || shouldShowReplaceUploadAction,
      showAttachSignature: canShowAttachSignature,
      showDeleteSignature: canDeleteSignature(doc, localFile, signatureValidationState),
      showDeleteDocument: canDeleteFile(doc, localFile) || canRemovePendingRow,
      showPreview: hasFile,
      showDownload: hasFile,
      showRegenerate: canRegenerateDocument,
    }),
    [
      canRemovePendingRow,
      canRegenerateDocument,
      canShowAttachSignature,
      doc,
      hasFile,
      localFile,
      shouldShowReplaceUploadAction,
      shouldShowRequiredUploadAction,
      signatureValidationState,
    ]
  );

  if (mode === 'document') {
    if (!isNewApplication) {
      return (
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onOpenCovenants();
            }}
            sx={appFormStyles.covenantActionButton}
          >
            {DOC_ACTION_LABELS.viewCovenant}
          </Button>

          <RequiredDocumentUploadConfirmModal
            open={isConfirmModalOpen}
            status={confirmableStatus}
            onConfirm={onConfirmUpload}
            onClose={onCloseConfirmModal}
          />
        </>
      );
    }

    return (
      <>
        <Button
          variant="outlined"
          size="small"
          onClick={handleMenuOpen}
          sx={appFormStyles.mainActionButton}
          endIcon={isMenuOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          aria-controls={isMenuOpen ? documentMenuId : undefined}
          aria-haspopup="true"
          aria-expanded={isMenuOpen ? 'true' : undefined}
        >
          {DOC_ACTION_LABELS.chooseAction}
        </Button>

        <Menu
          id={documentMenuId}
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          disableScrollLock
          transformOrigin={DOC_ACTION_MENU_ORIGIN.transform}
          anchorOrigin={DOC_ACTION_MENU_ORIGIN.anchor}
          slotProps={actionMenuSlotProps}
        >
          <MenuItem onClick={() => runMenuAction(onUploadClick)} sx={appFormStyles.actionMenuItem}>
            <ListItemText primary={DOC_ACTION_LABELS.uploadDocument} />
          </MenuItem>
          <MenuItem
            onClick={() => runMenuAction(onOpenCovenants)}
            sx={appFormStyles.actionMenuItem}
          >
            <ListItemText primary={DOC_ACTION_LABELS.viewCovenant} />
          </MenuItem>
          {shouldShowGenerateDocumentAction && (
            <MenuItem onClick={() => runMenuAction(openHistory)} sx={appFormStyles.actionMenuItem}>
              <ListItemText primary={DOC_ACTION_LABELS.generateDocument} />
            </MenuItem>
          )}
        </Menu>

        {documentGenerationDialogs}

        <RequiredDocumentUploadConfirmModal
          open={isConfirmModalOpen}
          status={confirmableStatus}
          onConfirm={onConfirmUpload}
          onClose={onCloseConfirmModal}
        />
      </>
    );
  }

  if (
    !fileMenuActions.showUpload &&
    !fileMenuActions.showAttachSignature &&
    !fileMenuActions.showDeleteSignature &&
    !fileMenuActions.showDeleteDocument &&
    !fileMenuActions.showPreview &&
    !fileMenuActions.showDownload &&
    !fileMenuActions.showRegenerate
  ) {
    return (
      <RequiredDocumentUploadConfirmModal
        open={isConfirmModalOpen}
        status={confirmableStatus}
        onConfirm={onConfirmUpload}
        onClose={onCloseConfirmModal}
      />
    );
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={appFormStyles.fileActionMenuButton(isMenuOpen)}
        aria-label={DOC_ACTION_LABELS.fileActionsAria}
        aria-controls={isMenuOpen ? fileMenuId : undefined}
        aria-haspopup="true"
        aria-expanded={isMenuOpen ? 'true' : undefined}
      >
        {isMenuOpen ? (
          <CloseIcon fontSize="small" sx={appFormStyles.fileActionMenuIcon} />
        ) : (
          <Box component="span" sx={appFormStyles.fileActionMenuDots} aria-hidden="true">
            <Box component="span" sx={appFormStyles.fileActionMenuDot} />
            <Box component="span" sx={appFormStyles.fileActionMenuDot} />
            <Box component="span" sx={appFormStyles.fileActionMenuDot} />
          </Box>
        )}
      </IconButton>

      <Menu
        id={fileMenuId}
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        disableScrollLock
        transformOrigin={DOC_ACTION_MENU_ORIGIN.transform}
        anchorOrigin={DOC_ACTION_MENU_ORIGIN.anchor}
        slotProps={actionMenuSlotProps}
      >
        {fileMenuActions.showUpload && (
          <MenuItem onClick={() => runMenuAction(onUploadClick)} sx={appFormStyles.actionMenuItem}>
            <ListItemText primary={DOC_ACTION_LABELS.uploadDocument} />
            <ListItemIcon sx={appFormStyles.actionMenuItemIcon}>
              <CloudUploadOutlinedIcon fontSize="small" />
            </ListItemIcon>
          </MenuItem>
        )}

        {fileMenuActions.showAttachSignature && (
          <MenuItem
            onClick={() => runMenuAction(handleUploadSignatureClick)}
            sx={appFormStyles.actionMenuItem}
          >
            <ListItemText primary={DOC_ACTION_LABELS.attachSignature} />
            <ListItemIcon sx={appFormStyles.actionMenuItemIcon}>
              <KeyOutlinedIcon fontSize="small" />
            </ListItemIcon>
          </MenuItem>
        )}

        {fileMenuActions.showPreview && (
          <MenuItem
            onClick={() => runMenuAction(handlePreviewClick)}
            sx={appFormStyles.actionMenuItem}
          >
            <ListItemText primary={DOC_ACTION_LABELS.previewDocument} />
            <ListItemIcon sx={appFormStyles.actionMenuItemIcon}>
              <VisibilityOutlinedIcon fontSize="small" />
            </ListItemIcon>
          </MenuItem>
        )}

        {fileMenuActions.showDownload && (
          <MenuItem
            onClick={() => runMenuAction(handleDownloadClick)}
            sx={appFormStyles.actionMenuItem}
          >
            <ListItemText primary={DOC_ACTION_LABELS.downloadDocument} />
            <ListItemIcon sx={appFormStyles.actionMenuItemIcon}>
              <DownloadOutlinedIcon fontSize="small" />
            </ListItemIcon>
          </MenuItem>
        )}

        {fileMenuActions.showRegenerate && (
          <MenuItem
            onClick={() => runMenuAction(() => void openRegeneration())}
            sx={appFormStyles.actionMenuItem}
          >
            <ListItemText primary={DOCUMENT_GENERATION_LABELS.regenerate} />
          </MenuItem>
        )}

        {fileMenuActions.showDeleteSignature && (
          <MenuItem
            onClick={() => runMenuAction(handleDeleteSignatureClick)}
            sx={appFormStyles.actionMenuItem}
          >
            <ListItemText primary={DELETE_SIGNATURE_LABEL} />
            <ListItemIcon sx={appFormStyles.actionMenuItemIcon}>
              <KeyOffOutlinedIcon fontSize="small" />
            </ListItemIcon>
          </MenuItem>
        )}

        {fileMenuActions.showDeleteDocument && (
          <MenuItem
            onClick={() => runMenuAction(handleDeleteDocumentClick)}
            sx={appFormStyles.actionMenuItemDanger}
          >
            <ListItemText primary={DOC_ACTION_LABELS.deleteDocument} />
            <ListItemIcon sx={appFormStyles.actionMenuItemDangerIcon}>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
          </MenuItem>
        )}
      </Menu>

      {documentGenerationDialogs}

      <RequiredDocumentUploadConfirmModal
        open={isConfirmModalOpen}
        status={confirmableStatus}
        onConfirm={onConfirmUpload}
        onClose={onCloseConfirmModal}
      />
    </>
  );
};
